import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { Phone, MessageCircle, CheckCircle2, ShieldCheck, Star, ArrowRight, Wrench, Calendar, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 *  10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

// === Update this path after uploading service hero image to 10Web Media Library ===
// Suggestion: technician with clipboard/tools next to a clean modern garage door,
// or a maintenance checklist scene. WebP 1920×1080 ~100KB.
const HERO_IMAGE_URL = "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/98e6b5da-5f28-4e7a-9644-2bf8351ab70d-1.webp";
const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const TRUST_BULLETS = ["12-point inspection — every visit, every door", "Catch worn springs, cables & rollers early", "Smooth, quiet operation guaranteed"];
const SERVICE_OPTIONS = ["One-off annual tune-up", "Annual maintenance plan", "Premium 6-month plan", "Pre-sale property check", "Post-storm safety check", "Just had it installed — first service"];
export default function ServiceHero() {
  const [service, setService] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [suburb, setSuburb] = useState("");
  return <section id="contact-us" className="relative overflow-hidden scroll-mt-28 !bg-[url(https://goldgaragedoor.com.au/wp-content/uploads/2026/06/PHOTO-2026-06-23-10-44-32.webp)] !bg-[url(https://goldgaragedoor.com.au/wp-content/uploads/2026/06/98e6b5da-5f28-4e7a-9644-2bf8351ab70d-1.webp)]" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "120px",
    paddingBottom: "100px"
  }}>
      {/* Hero background image with dark left mask */}
      <div aria-hidden className="absolute inset-0" style={{
      backgroundImage: `url(${HERO_IMAGE_URL})`,
      backgroundSize: "cover",
      backgroundPosition: "center right",
      opacity: 0.32
    }} />
      <div aria-hidden className="absolute inset-0" style={{
      background: `linear-gradient(90deg, ${NAVY_DEEP} 0%, ${NAVY_DEEP}EE 35%, ${NAVY}AA 65%, ${NAVY}55 100%)`
    }} />
      <div aria-hidden className="absolute inset-0 opacity-[0.05] !bg-[url(https://goldgaragedoor.com.au/wp-content/uploads/2026/06/98e6b5da-5f28-4e7a-9644-2bf8351ab70d-1.webp)]" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD} 50%, transparent)`
    }} />

      {/* Watermark numeral */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      right: "-40px",
      top: "60px",
      fontFamily: FONT_HEAD,
      fontSize: "260px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}1A`
    }}>
        2.5
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-start">
          {/* LEFT — copy + CTAs */}
          <div className="text-white">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 mb-7 px-4 py-2 rounded-full border" style={{
            borderColor: `${GOLD}55`,
            backgroundColor: `${GOLD}10`
          }}>
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <SafeHydrate><Calendar size={14} color={GOLD} strokeWidth={2.5} /></SafeHydrate>
              </span>
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                Annual Service &amp; Tune-Up · Sydney
              </span>
            </div>

            {/* H1 */}
            <h1 style={{
            fontFamily: FONT_HEAD,
            fontWeight: 800,
            fontSize: "clamp(2.5rem, 5.2vw, 4.5rem)",
            lineHeight: 1.04,
            letterSpacing: "-0.025em",
            marginBottom: "20px"
          }}>
              You service the car.
              <br />
              <span style={{
              color: GOLD
            }}>
                The garage door
              </span>
              <br />
              deserves the same.
            </h1>

            {/* Sub */}
            <p style={{
            fontFamily: FONT_BODY,
            fontSize: "clamp(1.05rem, 1.4vw, 1.2rem)",
            lineHeight: 1.6,
            color: "rgba(255,255,255,0.82)",
            maxWidth: "560px",
            marginBottom: "28px"
          }}>
              250+ moving parts. 1,500+ open-and-close cycles a year. A
              one-hour annual tune-up catches worn springs, cables and rollers{" "}
              <em>before</em> they snap on a 38°C Friday evening with the car
              boxed in.
            </p>

            {/* Trust bullets */}
            <ul className="mb-9 space-y-2.5" style={{
            maxWidth: "560px"
          }}>
              {TRUST_BULLETS.map(bullet => <li key={bullet} className="flex items-start gap-3" style={{
              fontFamily: FONT_BODY,
              fontSize: "15px",
              color: "rgba(255,255,255,0.9)"
            }}>
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex flex-shrink-0 mt-0.5">
                    <CheckCircle2 size={18} color={GOLD} strokeWidth={2.5} />
                  </span>
                  <span>{bullet}</span>
                </li>)}
            </ul>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 mb-9">
              <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-7 py-4 rounded-full transition-all hover:scale-[1.02] hover:shadow-xl" style={{
              backgroundColor: GOLD,
              color: NAVY,
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "16px",
              boxShadow: `0 10px 30px ${GOLD}33`
            }}>
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <Phone size={18} strokeWidth={2.5} />
                </span>
                Call 0485 000 933
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <ArrowRight size={16} strokeWidth={2.5} />
                </span>
              </a>

              <a href="https://wa.me/61485000933?text=Hi%2C%20I%27d%20like%20to%20book%20an%20annual%20garage%20door%20service.%20" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 px-6 py-4 rounded-full border-2 transition-all hover:scale-[1.02]" style={{
              borderColor: "#25D366",
              backgroundColor: "#25D366",
              color: "#081438",
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "15px"
            }}>
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <MessageCircle size={18} strokeWidth={2.5} />
                </span>
                WhatsApp Us
              </a>
            </div>

            {/* Trust strip */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-6 border-t" style={{
            borderColor: "rgba(255,255,255,0.12)"
          }}>
              <div className="flex items-center gap-2">
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <ShieldCheck size={16} color={GOLD} strokeWidth={2.5} />
                </span>
                <span style={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                color: "rgba(255,255,255,0.78)",
                fontWeight: 600
              }}>
                  1-Year Labour Warranty
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[0, 1, 2, 3, 4].map(i => <span key={i} aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <Star size={14} color={GOLD} fill={GOLD} strokeWidth={0} />
                    </span>)}
                </div>
                <span style={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                color: "rgba(255,255,255,0.78)",
                fontWeight: 600
              }}>5.0 · 300+ reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <Sparkles size={16} color={GOLD} strokeWidth={2.5} />
                </span>
                <span style={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                color: "rgba(255,255,255,0.78)",
                fontWeight: 600
              }}>
                  12-point inspection
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT — quote form card */}
          <div className="relative rounded-2xl overflow-hidden" style={{
          backgroundColor: "#FAF7F1",
          boxShadow: "0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(245,166,35,0.25)"
        }}>
            {/* Gold top edge */}
            <div aria-hidden className="absolute top-0 left-0 right-0 h-1" style={{
            background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`
          }} />

            <div className="p-7 md:p-9">
              {/* Header */}
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full" style={{
                backgroundColor: `${GOLD}15`
              }}>
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                    <Wrench size={12} color={GOLD_DARK} strokeWidth={2.5} />
                  </span>
                  <span style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: GOLD_DARK
                }}>
                    Free quote — under 60 seconds
                  </span>
                </div>
                <h2 style={{
                fontFamily: FONT_HEAD,
                fontWeight: 800,
                fontSize: "1.65rem",
                lineHeight: 1.15,
                color: NAVY,
                letterSpacing: "-0.02em"
              }}>
                  Book a service —
                  <br />
                  flat-price, no surprises.
                </h2>
              </div>

              <form onSubmit={e => {
              e.preventDefault();
            }} className="space-y-4">
                {/* Service dropdown */}
                <div>
                  <label htmlFor="service-type" className="block mb-1.5" style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  color: NAVY
                }}>
                    What kind of service?
                  </label>
                  <select id="service-type" value={service} onChange={e => setService(e.target.value)} className="w-full px-4 py-3 rounded-lg border bg-white" style={{
                  fontFamily: FONT_BODY,
                  fontSize: "15px",
                  color: NAVY,
                  borderColor: "rgba(8,20,56,0.18)"
                }}>
                    <option value="">— Select an option —</option>
                    {SERVICE_OPTIONS.map(s => <option key={s} value={s}>
                        {s}
                      </option>)}
                    <option value="Not sure">Not sure — need advice</option>
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="service-name" className="block mb-1.5" style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  color: NAVY
                }}>
                    Your name
                  </label>
                  <input id="service-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="First name" className="w-full px-4 py-3 rounded-lg border bg-white" style={{
                  fontFamily: FONT_BODY,
                  fontSize: "15px",
                  color: NAVY,
                  borderColor: "rgba(8,20,56,0.18)"
                }} />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="service-phone" className="block mb-1.5" style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  color: NAVY
                }}>
                    Mobile
                  </label>
                  <input id="service-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="04XX XXX XXX" className="w-full px-4 py-3 rounded-lg border bg-white" style={{
                  fontFamily: FONT_BODY,
                  fontSize: "15px",
                  color: NAVY,
                  borderColor: "rgba(8,20,56,0.18)"
                }} />
                </div>

                {/* Suburb */}
                <div>
                  <label htmlFor="service-suburb" className="block mb-1.5" style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  color: NAVY
                }}>
                    Suburb
                  </label>
                  <input id="service-suburb" type="text" value={suburb} onChange={e => setSuburb(e.target.value)} placeholder="e.g. Bondi, Parramatta…" className="w-full px-4 py-3 rounded-lg border bg-white" style={{
                  fontFamily: FONT_BODY,
                  fontSize: "15px",
                  color: NAVY,
                  borderColor: "rgba(8,20,56,0.18)"
                }} />
                </div>

                {/* Submit */}
                <button type="submit" className="w-full py-4 rounded-lg transition-all hover:scale-[1.01]" style={{
                backgroundColor: NAVY,
                color: "#ffffff",
                fontFamily: FONT_HEAD,
                fontWeight: 700,
                fontSize: "15px",
                letterSpacing: "0.02em",
                boxShadow: `0 8px 24px ${NAVY}55`
              }}>
                  Get my free quote →
                </button>

                <p style={{
                fontFamily: FONT_BODY,
                fontSize: "12px",
                color: "rgba(8,20,56,0.55)",
                textAlign: "center",
                marginTop: "8px"
              }}>
                  No obligation. We respond in minutes during business hours.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}